import { useState, useEffect, useCallback } from 'react';
import { Plus, Edit, Trash2, CheckCircle, XCircle, AlertTriangle } from 'lucide-react';
import { projectRaidService } from '../../services/projectRaid';
import { OwnerSelect } from '../common/OwnerSelect';
import { toast } from 'sonner';

interface Assumption {
  id: string;
  description: string;
  category: string;
  impact: 'High' | 'Medium' | 'Low';
  owner: string;
  validationStatus: 'Valid' | 'Invalid' | 'Pending Validation';
  validationDate?: string;
  notes: string;
}

interface AssumptionsSectionProps {
  projectId?: number;
  onChanged?: () => void;
}

function mapRaidToAssumption(item: { id: number; title: string | null; description: string; owner: string | null; extra_metadata: Record<string, unknown> | null }): Assumption {
  const meta = item.extra_metadata || {};
  return {
    id: `A-${String(item.id).padStart(3, '0')}`,
    description: item.description || item.title || '',
    category: (meta.category as string) || 'Other',
    impact: (meta.impact as 'High' | 'Medium' | 'Low') || 'Medium',
    owner: item.owner || '',
    validationStatus: (meta.validationStatus as 'Valid' | 'Invalid' | 'Pending Validation') || 'Pending Validation',
    validationDate: (meta.validationDate as string) || undefined,
    notes: (meta.notes as string) || '',
  };
}

function assumptionToRaidBody(a: Assumption) {
  return {
    raid_type: 'assumption' as const,
    title: a.description.slice(0, 120) || 'Assumption',
    description: a.description,
    owner: a.owner || null,
    extra_metadata: {
      category: a.category,
      impact: a.impact,
      validationStatus: a.validationStatus,
      validationDate: a.validationDate,
      notes: a.notes,
    },
  };
}

export function AssumptionsSection({ projectId, onChanged }: AssumptionsSectionProps) {
  const [showForm, setShowForm] = useState(false);
  const [editingAssumption, setEditingAssumption] = useState<Assumption | null>(null);
  const [assumptions, setAssumptions] = useState<Assumption[]>([]);

  const loadAssumptions = useCallback(() => {
    if (!projectId) {
      setAssumptions([]);
      return;
    }
    projectRaidService
      .list(projectId, 'assumption')
      .then((list) => setAssumptions(list.map(mapRaidToAssumption)))
      .catch(() => {
        toast.error('Failed to load assumptions');
        // Do NOT fall back to demo data on real projects — see RisksSection.
        setAssumptions([]);
      });
  }, [projectId]);

  useEffect(() => {
    loadAssumptions();
  }, [loadAssumptions]);

  const handleAddAssumption = () => {
    setEditingAssumption(null);
    setShowForm(true);
  };

  const handleEditAssumption = (assumption: Assumption) => {
    setEditingAssumption(assumption);
    setShowForm(true);
  };

  const handleSaveAssumption = async (assumption: Assumption) => {
    if (!projectId) {
      // Demo/static mode: keep prior local-only behaviour.
      if (editingAssumption) {
        setAssumptions(assumptions.map((a) => (a.id === assumption.id ? assumption : a)));
      } else {
        setAssumptions([
          ...assumptions,
          { ...assumption, id: `A-${String(assumptions.length + 1).padStart(3, '0')}` },
        ]);
      }
      setShowForm(false);
      setEditingAssumption(null);
      return;
    }
    try {
      if (editingAssumption) {
        const numericId = parseInt(editingAssumption.id.replace(/[^0-9]/g, ''), 10);
        await projectRaidService.update(projectId, numericId, assumptionToRaidBody(assumption));
        toast.success('Assumption updated');
      } else {
        await projectRaidService.create(projectId, assumptionToRaidBody(assumption));
        toast.success('Assumption added');
      }
      setShowForm(false);
      setEditingAssumption(null);
      loadAssumptions();
      onChanged?.();
    } catch {
      toast.error('Failed to save assumption');
    }
  };

  const handleDeleteAssumption = async (id: string) => {
    if (!projectId) {
      setAssumptions(assumptions.filter((a) => a.id !== id));
      return;
    }
    try {
      const numericId = parseInt(id.replace(/[^0-9]/g, ''), 10);
      await projectRaidService.delete(projectId, numericId);
      loadAssumptions();
      onChanged?.();
    } catch {
      toast.error('Failed to delete assumption');
    }
  };

  const getStatusColor = (status: string) => {
    if (status === 'Valid') return 'bg-green-50 text-green-700 border-green-200';
    if (status === 'Invalid') return 'bg-red-50 text-red-700 border-red-200';
    return 'bg-yellow-50 text-yellow-700 border-yellow-200';
  };

  const getImpactColor = (impact: string) => {
    if (impact === 'High') return 'bg-red-50 text-red-700 border-red-200';
    if (impact === 'Medium') return 'bg-orange-50 text-orange-700 border-orange-200';
    return 'bg-blue-50 text-blue-700 border-blue-200';
  };

  const metrics = {
    total: assumptions.length,
    valid: assumptions.filter((a) => a.validationStatus === 'Valid').length,
    invalid: assumptions.filter((a) => a.validationStatus === 'Invalid').length,
    pending: assumptions.filter((a) => a.validationStatus === 'Pending Validation').length,
  };

  return (
    <div className="p-6 space-y-6">
      {/* Summary Metrics */}
      <div className="grid grid-cols-4 gap-6">
        <div className="bg-white p-5 rounded-lg border border-gray-200">
          <div className="flex items-center justify-between mb-2">
            <div className="text-sm text-gray-600">Total Assumptions</div>
            <AlertTriangle className="w-4 h-4 text-blue-600" />
          </div>
          <div className="text-3xl text-neutral-950">{metrics.total}</div>
        </div>
        <div className="bg-white p-5 rounded-lg border border-gray-200">
          <div className="flex items-center justify-between mb-2">
            <div className="text-sm text-gray-600">Valid</div>
            <CheckCircle className="w-4 h-4 text-green-600" />
          </div>
          <div className="text-3xl text-neutral-950">{metrics.valid}</div>
        </div>
        <div className="bg-white p-5 rounded-lg border border-gray-200">
          <div className="flex items-center justify-between mb-2">
            <div className="text-sm text-gray-600">Invalid</div>
            <XCircle className="w-4 h-4 text-red-600" />
          </div>
          <div className="text-3xl text-neutral-950">{metrics.invalid}</div>
        </div>
        <div className="bg-white p-5 rounded-lg border border-gray-200">
          <div className="flex items-center justify-between mb-2">
            <div className="text-sm text-gray-600">Pending</div>
            <AlertTriangle className="w-4 h-4 text-yellow-600" />
          </div>
          <div className="text-3xl text-neutral-950">{metrics.pending}</div>
        </div>
      </div>

      {/* Action Bar */}
      <div className="flex items-center justify-end">
        <button
          onClick={handleAddAssumption}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          <Plus className="w-4 h-4" />
          Add Assumption
        </button>
      </div>

      {/* Assumptions Table */}
      <div className="bg-white rounded-lg border border-gray-200">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-left py-4 px-6 text-sm text-neutral-950">ID</th>
                <th className="text-left py-4 px-6 text-sm text-neutral-950">Description</th>
                <th className="text-left py-4 px-6 text-sm text-neutral-950">Category</th>
                <th className="text-left py-4 px-6 text-sm text-neutral-950">Impact</th>
                <th className="text-left py-4 px-6 text-sm text-neutral-950">Owner</th>
                <th className="text-left py-4 px-6 text-sm text-neutral-950">Status</th>
                <th className="text-left py-4 px-6 text-sm text-neutral-950">Validation Date</th>
                <th className="text-left py-4 px-6 text-sm text-neutral-950">Actions</th>
              </tr>
            </thead>
            <tbody>
              {assumptions.map((assumption) => (
                <tr key={assumption.id} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="py-4 px-6 text-sm text-gray-600">{assumption.id}</td>
                  <td className="py-4 px-6">
                    <div className="text-sm text-neutral-950 max-w-md">{assumption.description}</div>
                    {assumption.notes && (
                      <div className="text-xs text-gray-500 mt-1">{assumption.notes}</div>
                    )}
                  </td>
                  <td className="py-4 px-6">
                    <span className="px-3 py-1 bg-purple-50 text-purple-700 rounded text-xs border border-purple-200">
                      {assumption.category}
                    </span>
                  </td>
                  <td className="py-4 px-6">
                    <span className={`px-3 py-1 rounded text-xs border ${getImpactColor(assumption.impact)}`}>
                      {assumption.impact}
                    </span>
                  </td>
                  <td className="py-4 px-6 text-sm text-gray-600">{assumption.owner}</td>
                  <td className="py-4 px-6">
                    <span className={`px-3 py-1 rounded text-xs border ${getStatusColor(assumption.validationStatus)}`}>
                      {assumption.validationStatus}
                    </span>
                  </td>
                  <td className="py-4 px-6 text-sm text-gray-600">
                    {assumption.validationDate || '-'}
                  </td>
                  <td className="py-4 px-6">
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleEditAssumption(assumption)}
                        className="p-1 text-blue-600 hover:bg-blue-50 rounded"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteAssumption(assumption.id)}
                        className="p-1 text-red-600 hover:bg-red-50 rounded"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add/Edit Form Modal */}
      {showForm && (
        <AssumptionForm
          assumption={editingAssumption}
          onClose={() => {
            setShowForm(false);
            setEditingAssumption(null);
          }}
          onSave={handleSaveAssumption}
        />
      )}
    </div>
  );
}

// Assumption Form Component
function AssumptionForm({
  assumption,
  onClose,
  onSave,
}: {
  assumption: Assumption | null;
  onClose: () => void;
  onSave: (assumption: Assumption) => void;
}) {
  const [formData, setFormData] = useState<Assumption>(
    assumption || {
      id: '',
      description: '',
      category: 'Technical',
      impact: 'Medium',
      owner: '',
      validationStatus: 'Pending Validation',
      notes: '',
    }
  );

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl">
        <div className="border-b border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl text-neutral-950">
              {assumption ? 'Edit Assumption' : 'Add Assumption'}
            </h2>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl">
              ×
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div>
            <label className="block text-sm mb-2 text-neutral-950">Description *</label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows={3}
              placeholder="Describe the assumption"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-6">
            <div>
              <label className="block text-sm mb-2 text-neutral-950">Category *</label>
              <select
                name="category"
                value={formData.category}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              >
                <option value="Technical">Technical</option>
                <option value="Resources">Resources</option>
                <option value="Financial">Financial</option>
                <option value="Organizational">Organizational</option>
                <option value="Schedule">Schedule</option>
              </select>
            </div>
            <div>
              <label className="block text-sm mb-2 text-neutral-950">Impact *</label>
              <select
                name="impact"
                value={formData.impact}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              >
                <option value="High">High</option>
                <option value="Medium">Medium</option>
                <option value="Low">Low</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-6">
            <div>
              <label className="block text-sm mb-2 text-neutral-950">Owner</label>
              <OwnerSelect
                name="owner"
                value={formData.owner}
                onChange={(v) => setFormData({ ...formData, owner: v })}
              />
            </div>
            <div>
              <label className="block text-sm mb-2 text-neutral-950">Validation Status *</label>
              <select
                name="validationStatus"
                value={formData.validationStatus}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              >
                <option value="Pending Validation">Pending Validation</option>
                <option value="Valid">Valid</option>
                <option value="Invalid">Invalid</option>
              </select>
            </div>
          </div>

          {formData.validationStatus !== 'Pending Validation' && (
            <div>
              <label className="block text-sm mb-2 text-neutral-950">Validation Date</label>
              <input
                type="date"
                name="validationDate"
                value={formData.validationDate || ''}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          )}

          <div>
            <label className="block text-sm mb-2 text-neutral-950">Notes</label>
            <textarea
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              rows={2}
              placeholder="Additional notes or validation details"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div className="flex justify-end gap-3 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              {assumption ? 'Update' : 'Add'} Assumption
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
